import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Clock, CalendarCheck, Sparkles, Users } from 'lucide-react';
import { Button } from './ui/button';
import { restaurantInfo } from '../data/mock';
import { openingHoursData } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';
import { getForatableModalUrl } from '../lib/foratable';

const Contact = () => {
  const { t, language } = useLanguage();
  const [currentDay, setCurrentDay] = useState('');

  useEffect(() => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const today = new Date().getDay();
    setCurrentDay(days[today]);
  }, []);

  const getDay = (dayKey) => t(`days.${dayKey}`);
  const getClosed = () => t('contact.closed');

  // ForaTable's own script (iframeContainers.js) opens the popup when a
  // button carrying data-lunchgate-modal-url is clicked — no React modal needed.
  const foratableModalUrl = getForatableModalUrl(language);

  return (
    <section id="contact" className="py-24 bg-stone-950 relative overflow-hidden">
      {/* Top Border */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-700/50 to-transparent" />

      {/* Decorative Elements */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-amber-600/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-64 h-64 bg-amber-700/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-amber-500 uppercase tracking-[0.3em] text-sm mb-4">
            {t('contact.subtitle')}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-white">
            {t('contact.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-6" />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info & Hours */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-stone-900/50 backdrop-blur-sm border border-stone-800 rounded-2xl p-6 group hover:border-amber-700/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">{t('contact.address')}</h3>
                <p className="text-amber-100/70 text-sm">{restaurantInfo.address}</p>
              </div>

              <div className="bg-stone-900/50 backdrop-blur-sm border border-stone-800 rounded-2xl p-6 group hover:border-amber-700/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">{t('contact.phone')}</h3>
                <a href="tel:+41326236888" className="text-amber-100/70 text-sm hover:text-amber-400 transition-colors">
                  +41 32 623 68 88
                </a>
              </div>

              <div className="bg-stone-900/50 backdrop-blur-sm border border-stone-800 rounded-2xl p-6 group hover:border-amber-700/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">{t('contact.email')}</h3>
                <a href={`mailto:${restaurantInfo.email}`} className="text-amber-100/70 text-sm hover:text-amber-400 transition-colors">
                  {restaurantInfo.email}
                </a>
              </div>

              <div className="bg-stone-900/50 backdrop-blur-sm border border-stone-800 rounded-2xl p-6 group hover:border-amber-700/50 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-white font-semibold mb-2">{t('contact.hours')}</h3>
                <p className="text-amber-100/70 text-sm">↓</p>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="bg-stone-900/50 backdrop-blur-sm border border-stone-800 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                {t('contact.hoursTitle')}
              </h3>
              <div className="space-y-2">
                {openingHoursData.map((item, index) => {
                  const isClosed = item.hours === 'closed';
                  const isToday = item.dayKey === currentDay;
                  return (
                    <div
                      key={index}
                      className={`flex justify-between items-center py-3 px-4 rounded-lg transition-all duration-300 ${
                        isToday 
                          ? 'bg-amber-600/20 border border-amber-500/50 shadow-lg shadow-amber-900/20' 
                          : 'border border-transparent hover:bg-stone-800/30'
                      } ${isClosed && !isToday ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        {isToday && (
                          <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                        )}
                        <span className={`font-medium ${isToday ? 'text-amber-200' : 'text-amber-100/80'}`}>
                          {getDay(item.dayKey)}
                        </span>
                        {isToday && (
                          <span className="text-xs bg-amber-600/30 text-amber-400 px-2 py-0.5 rounded-full ml-2">
                            {language === 'de' ? 'Heute' : language === 'fr' ? "Aujourd'hui" : language === 'en' ? 'Today' : 'Oggi'}
                          </span>
                        )}
                      </div>
                      <span className={`text-sm ${
                        isClosed 
                          ? 'text-red-400' 
                          : isToday 
                            ? 'text-amber-300 font-semibold' 
                            : 'text-amber-100/60'
                      }`}>
                        {isClosed ? getClosed() : item.hours}
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Hot kitchen notice */}
              <div className="mt-4 pt-4 border-t border-stone-800/80 flex items-start gap-2">
                <span className="text-amber-400 mt-0.5" aria-hidden="true">🍳</span>
                <p className="text-amber-100/70 text-sm leading-relaxed italic" data-testid="hot-kitchen-notice">
                  {t('contact.hotKitchen')}
                </p>
              </div>
            </div>
          </div>

          {/* Reservation CTA — opens ForaTable in modal */}
          <div className="relative bg-gradient-to-br from-stone-900/80 via-stone-900/60 to-amber-950/30 backdrop-blur-sm border border-amber-700/30 rounded-2xl p-8 lg:p-10 overflow-hidden flex flex-col">
            {/* Decorative ornaments */}
            <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 bg-amber-500/10 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-20 w-64 h-64 bg-amber-700/10 rounded-full blur-3xl" />

            <div className="relative flex flex-col items-center text-center flex-1">
              {/* Icon */}
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center mb-6 shadow-lg shadow-amber-900/40 ring-4 ring-amber-600/10">
                <CalendarCheck className="w-8 h-8 text-white" />
              </div>

              {/* Headline */}
              <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-3 leading-tight">
                {t('contact.formTitle')}
              </h3>

              {/* Subtitle */}
              <p className="text-amber-100/70 text-base leading-relaxed max-w-md mb-8">
                {t('contact.reserveSubtitle')}
              </p>

              {/* Primary CTA — ForaTable popup widget */}
              <Button
                data-lunchgate-modal-url={foratableModalUrl}
                data-testid="open-foratable-modal"
                className="group w-full max-w-sm bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white py-7 rounded-xl text-lg font-medium transition-all hover:scale-[1.02] shadow-xl shadow-amber-900/40 ripple"
              >
                <Sparkles className="w-5 h-5 mr-2 transition-transform group-hover:rotate-12" />
                {t('contact.reserveButton')}
              </Button>

              {/* Divider with "or" */}
              <div className="flex items-center gap-4 w-full max-w-sm my-7">
                <div className="h-px flex-1 bg-stone-700/60" />
                <span className="text-amber-100/40 text-xs uppercase tracking-widest">
                  {t('contact.reserveOr')}
                </span>
                <div className="h-px flex-1 bg-stone-700/60" />
              </div>

              {/* Phone fallback */}
              <p className="text-amber-100/50 text-sm mb-3">
                {t('contact.reserveCallUs')}
              </p>
              <a
                href="tel:+41326236888"
                data-testid="reserve-phone-link"
                className="group inline-flex items-center gap-3 px-6 py-3 rounded-full border border-amber-600/40 hover:border-amber-400 hover:bg-amber-600/10 transition-all"
              >
                <Phone className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" />
                <span className="text-amber-100 font-semibold tracking-wide">
                  +41 32 623 68 88
                </span>
              </a>

              {/* Groups & Private Events Box */}
              <div className="w-full max-w-sm mt-8 flex items-start gap-3 bg-stone-900/40 rounded-xl p-4 border border-stone-800/60 text-left">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-600/15 border border-amber-600/30 flex items-center justify-center">
                  <Users className="w-4 h-4 text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-amber-100 font-semibold text-xs mb-1" data-testid="groups-title">
                    {t('contact.groupsTitle')}
                  </h4>
                  <p className="text-amber-100/60 text-xs leading-relaxed">
                    {t('contact.groupsText')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map - Lighter style */}
        <div className="mt-16 rounded-2xl overflow-hidden border border-stone-800">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2714.8799673989784!2d7.536274776690167!3d47.20807661233778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4791e1c0a1a5c7af%3A0x8c8d8d8d8d8d8d8d!2sGoldgasse%2015%2C%204500%20Solothurn%2C%20Switzerland!5e0!3m2!1sen!2s!4v1620000000000!5m2!1sen!2s"
            width="100%"
            height="400"
            style={{ border: 0, filter: 'grayscale(50%) brightness(95%) contrast(95%)' }}
            allowFullScreen=""
            loading="lazy"
            title="Trattoria Da Daniele Location"
          />
        </div>
      </div>
    </section>
  );
};

export default Contact;
